import { TranslatedText } from '../translated-text';
import { useState, useEffect, useRef } from 'react';

/**
 * Landing Page Welcome Section
 * 
 * Displays welcome message with image and paper scroll animations
 */
export default function LandingWelcomeSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const previousY = useRef<number>(0);

  useEffect(() => {
    // Trigger text animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll-triggered animations
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Simply toggle based on intersection - animate both ways
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.2, // Trigger when 20% of section is visible
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before fully in view
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="w-full min-h-[60vh] relative" style={{ backgroundColor: '#000000' }}>
      {/* Main Content Section */}
      <div className="h-full max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-center min-h-[50vh]">
          {/* Left Side - Image with 3D zoom animation */}
          <div 
            className={`order-2 md:order-1 h-full min-h-[300px] md:min-h-[400px] overflow-hidden transition-all duration-1000 ease-out ${
              isInView 
                ? 'opacity-100' 
                : 'opacity-0'
            }`}
            style={{
              transform: isInView 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-300px) scale(0.6)'
            }}
          >
            <img
              src="/images/webflow/main.jpg"
              alt="Ikoro House Interior"
              className="w-full h-full object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Right Side - Text Content with 3D zoom animation */}
          <div 
            className={`order-1 md:order-2 space-y-4 transition-all duration-1000 delay-200 ease-out ${
              isInView 
                ? 'opacity-100' 
                : 'opacity-0'
            }`}
            style={{
              transform: isInView 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-300px) scale(0.6)'
            }}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white">
              <TranslatedText text="Welcome to Ikoro House Rebero" />
            </h2>
            <div className="space-y-3 text-sm sm:text-base md:text-base leading-relaxed text-white">
              {/* First paragraph - Animated */}
              <p 
                className={`transition-all duration-1000 ease-out ${
                  isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 -translate-y-10'
                }`}
              >
                <TranslatedText text="Discover Ikoro House, a boutique hotel located in Kwirebero, Rwanda, offering elegantly appointed rooms equipped with fully-furnished kitchens, comfortable beds, complimentary coffee, high-speed Wi-Fi, and premium bedding for your ultimate comfort. Each room is thoughtfully designed to provide a home-away-from-home experience with all the amenities you need." />
              </p>
              {/* Second paragraph - Animated with delay */}
              <p 
                className={`transition-all duration-1000 delay-300 ease-out ${
                  isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 -translate-y-10'
                }`}
              >
                <TranslatedText text="Experience exceptional hospitality with our 24/7 concierge service, savor delicious cuisine at our rustic bar and grill, and unwind in our comfortable lounge area. From spacious accommodations with private kitchens to attentive room service, we provide everything you need for an unforgettable stay. Let us exceed your expectations at Ikoro House - your gateway to Rwandan charm and comfort." />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


